import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { shouldRememberFilters } from "./userPreferences";

/**
 * Session-scoped state for grid filters, sorts and page sizes.
 *
 * The list screens unmount when the user navigates to another section, so any
 * filtering they had set up was lost. Backing the state with sessionStorage
 * keeps it for as long as the browser tab is open, and throws it away when the
 * tab is closed (or another user logs in — see clearPersistedGridState).
 *
 * Users who would rather start clean each time can turn this off under
 * "Remember filters" in their preferences, in which case this behaves as a
 * plain useState.
 */

const STORAGE_PREFIX = "bamfords.gridState.";

function readStored<T>(key: string, fallback: T): T {
  try {
    const stored = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (stored === null) return fallback;
    return JSON.parse(stored) as T;
  } catch (error) {
    // Corrupt entry, or storage unavailable (private browsing) — start fresh.
    console.warn(`Could not restore session state for "${key}":`, error);
    return fallback;
  }
}

/**
 * Drop-in replacement for useState that survives navigation away from the
 * screen. `key` must be unique per screen + value, e.g. "jobs.filter".
 */
export function useSessionState<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  // Read the preference once per mount rather than subscribing to it. Toggling
  // it is rare and deliberate, and re-reading here would swap a screen's
  // filters out from under the user while they are looking at it — saving the
  // preference clears the stored state instead, so the next screen starts clean.
  const [remember] = useState(shouldRememberFilters);

  const [value, setValue] = useState<T>(() =>
    remember ? readStored(key, initialValue) : initialValue
  );

  useEffect(() => {
    if (!remember) return;

    try {
      if (value === undefined) {
        sessionStorage.removeItem(STORAGE_PREFIX + key);
      } else {
        sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
      }
    } catch (error) {
      // Storage full or unavailable — the state still works, just not persisted.
      console.warn(`Could not persist session state for "${key}":`, error);
    }
  }, [key, value, remember]);

  return [value, setValue];
}

/**
 * Wipes every persisted grid filter. Used when a user logs in, and when the
 * Dashboard link in the sidebar is clicked so there is a deliberate way to
 * start again without closing the tab.
 *
 * Only clears storage — a list screen already on screen keeps its in-memory
 * state, because each one reads storage when it mounts. That is fine for the
 * cases above, since both navigate away from the list.
 */
export function clearPersistedGridState(): void {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn("Could not clear persisted grid state:", error);
  }
}
